import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { defaultProject, useUnityBuildStore } from '../stores/store';
import {
  useSyncedProjects,
  useSyncedCreateProjectMutation,
  useSyncedUpdateProjectMutation,
} from '../hooks/useSyncedProjects';
import { useFetchProjectHierarchy } from '../hooks/useFetchProjects';
import { useFetchUser } from '../hooks/useFetchUsers';
import { useStore } from 'zustand';
import { Project } from '../api/protosCompiled/project/project_pb';

type CreateProjectProps = {
  create: boolean;
  update: boolean;
  updateProjectId?: string;
  selectedHierarchyId?: string | null;
  setIsCreatingProject: (creatingNewProject: boolean) => void;
  setIsUpdatingProject: (updatingNewProject: boolean) => void;
  setIsCreatingLoading: (isLoading: boolean) => void;
};

export const CreateProject: React.FC<CreateProjectProps> = (props: CreateProjectProps) => {
  const theme = useTheme();

  const setIsCreatingNewProject = useStore(useUnityBuildStore).setIsCreatingNewProject;
  const { projects: projectList } = useSyncedProjects();
  const { mutate: createProject } = useSyncedCreateProjectMutation();
  const { mutate: updateProject } = useSyncedUpdateProjectMutation();
  const [project, setProject] = useState<Project.AsObject>(defaultProject);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get current user ID for hierarchy fetching
  const { data: currentUser } = useFetchUser();
  const { data: hierarchyList } = useFetchProjectHierarchy(currentUser?.user?.id || '');

  // Helper function to format date to "yyyy-MM-dd"
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Extract only the date part
  };

  // Inside your useEffect:
  useEffect(() => {
    if (props.update) {
      const matchingProject = projectList?.find(
        project => project.project?.id === props.updateProjectId
      );
      if (matchingProject) {
        const projectData = matchingProject.project!;
        setProject({
          ...projectData,
          startdate: projectData.startdate
            ? formatDate(matchingProject.project?.startdate ?? '')
            : '',
          enddate: projectData.enddate ? formatDate(matchingProject.project?.enddate ?? '') : '',
        });
      }
    } else if (props.create && props.selectedHierarchyId) {
      // Set the selected hierarchy ID when creating a new project
      setProject(prev => ({
        ...prev,
        projecthierarchyid: props.selectedHierarchyId || '',
      }));
    }
  }, [props.update, props.create, props.updateProjectId, props.selectedHierarchyId, projectList]);

  const defaultTodaysDate = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    const yyyy = today.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    props.setIsCreatingLoading(true);

    try {
      const formData: Project.AsObject = {
        id: project.id || '',
        name: project.name || '',
        address: project.address || '',
        description: project.description || '',
        startdate: project.startdate || defaultTodaysDate(),
        enddate: project.enddate || defaultTodaysDate(),
        datecreated: project.datecreated || '',
        openaivectorstoreid: project.openaivectorstoreid || '',
        openaiprojectassistantid: project.openaiprojectassistantid || '',
        createdbyuserid: project.createdbyuserid || '',
        createdbyaccountid: project.createdbyaccountid || '',
        projectcontractsList: project.projectcontractsList || [],
        projecthierarchyid: project.projecthierarchyid || '',
      };

      // Convert callbacks to promises
      const executeOperation = () => {
        return new Promise<void>((resolve, reject) => {
          if (props.update) {
            updateProject(formData, {
              onSuccess: () => resolve(),
              onError: error => reject(error),
            });
          } else {
            createProject(formData, {
              onSuccess: () => resolve(),
              onError: error => reject(error),
            });
          }
        });
      };

      await executeOperation();

      props.setIsCreatingProject(false);
      props.setIsUpdatingProject(false);
      resetForm();
    } catch (error) {
      console.error('Error creating/updating project:', error);
    } finally {
      setIsSubmitting(false);
      props.setIsCreatingLoading(false);
    }
  };

  const resetForm = () => {
    setProject(defaultProject);
    setIsCreatingNewProject(false);
  };

  return (
    <>
      {/* Loading Dialog */}
      <Dialog
        open={isSubmitting}
        disableEscapeKeyDown
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 1,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {props.update ? 'Updating Project' : 'Creating Project'}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 3,
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{
              color: theme.palette.primary.main,
            }}
          />
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ maxWidth: 300 }}
          >
            {props.update ? 'Updating your project...' : 'Creating your new project...'}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}
          >
            Please wait while we process your request...
          </Typography>
        </DialogContent>
      </Dialog>

      <Box sx={{ p: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, textAlign: 'center' }}>
          {props.update ? 'Update Project' : 'Create New Project'}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Project Name"
                autoFocus
                value={project.name}
                onChange={e => setProject({ ...project, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="address"
                label="Project Address"
                value={project.address}
                onChange={e => setProject({ ...project, address: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="description"
                label="Project Description"
                multiline
                rows={4}
                value={project.description}
                onChange={e => setProject({ ...project, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="hierarchy-select-label">Project Folder</InputLabel>
                <Select
                  labelId="hierarchy-select-label"
                  id="hierarchy-select"
                  value={project.projecthierarchyid || ''}
                  label="Project Folder"
                  onChange={e => setProject({ ...project, projecthierarchyid: e.target.value })}
                >
                  <MenuItem value="">
                    <em>No specific folder</em>
                  </MenuItem>
                  {hierarchyList?.map(hierarchy => (
                    <MenuItem key={hierarchy.id} value={hierarchy.id}>
                      {hierarchy.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="startDate"
                label="Construction Start Date"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={project.startdate || defaultTodaysDate()}
                onChange={e => setProject({ ...project, startdate: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="endDate"
                label="Construction End Date"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={project.enddate || defaultTodaysDate()}
                onChange={e => setProject({ ...project, enddate: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2, mb: 2 }}>
                {props.update ? 'Update Project' : 'Create Project'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};
